import { useDismiss } from '../dismiss.ts';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PinnedBar } from './PinnedBar.tsx';
import { Brand } from './Brand.tsx';
import type { MessageDTO } from '@tc/contracts';
import { discardOutbox, getState, loadOlder, openConversation, retryOutbox, scheduleMarkRead, useStore } from '../store.ts';
import { formatDay, languageName, useT, useUiLocale } from '../i18n.ts';
import { Composer } from './Composer.tsx';
import { api } from '../api.ts';
import { SummaryDialog } from './SummaryDialog.tsx';
import { hasVoice, speakMessage, stopSpeaking, ttsSupported, unlockSpeech } from '../tts.ts';
import { recordingSupported } from '../voice.ts';
import { Message } from './Message.tsx';
import { GroupInfo } from './GroupInfo.tsx';
import { SearchResults } from './Search.tsx';
import { Linkified, Seal, conversationLocales, conversationTitle } from './common.tsx';

const EMPTY: MessageDTO[] = [];

export function ThreadView() {
  const t = useT();
  const ui = useUiLocale();
  const activeId = useStore((s) => s.activeId);
  const conv = useStore((s) => (s.activeId ? s.conversations[s.activeId] : undefined));
  const thread = useStore((s) => (s.activeId ? s.threads[s.activeId] : undefined));
  const outbox = useStore((s) => s.outbox);
  const me = useStore((s) => s.me)!;
  const [replyTo, setReplyTo] = useState<MessageDTO | null>(null);
  const [editing, setEditing] = useState<MessageDTO | null>(null);
  // true = chat details open; 'files' = open and scrolled to the shared files.
  const [info, setInfo] = useState<boolean | 'files'>(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatQuery, setChatQuery] = useState('');
  const [dragging, setDragging] = useState(false);
  const presence = useStore((s) => s.presence);
  const typingMap = useStore((s) => (s.activeId ? s.typing[s.activeId] : undefined));
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // A tap beside the "…" menu closes it (and does nothing else).
  const moreRef = useRef<HTMLDivElement>(null);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  useDismiss(moreRef, moreOpen, closeMore, { swallow: true });
  // Each person in the chat gets their own name colour (in member order, so nobody in a chat shares one).
  const nameColors = useMemo(() => new Map((conv?.members ?? []).filter((x) => x.id !== me?.id).map((x, i) => [x.id, i] as const)), [conv?.members, me?.id]);
  // Unread messages in other chats, shown next to the back arrow (as phone messengers do).
  const otherUnread = useStore((s) => Object.values(s.conversations).reduce((n, c) => (c.id === s.activeId ? n : n + (c.unreadCount ?? 0)), 0));
  // Phone app: Android back button closes the details panel first.
  useEffect(() => {
    const close = () => setInfo(false);
    window.addEventListener('tc-close-info', close);
    return () => window.removeEventListener('tc-close-info', close);
  }, []);
  const swipe = useSwipeBack(() => void openConversation(null));
  const highlight = useStore((s) => s.highlight);
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const prevHeight = useRef(0);
  const prevFirst = useRef<string | undefined>(undefined);

  const items = thread?.items ?? EMPTY;
  const pending = outbox.filter((o) => o.conversationId === activeId);

  // ---- talk mode (walkie-talkie): speak → sent at once; incoming messages are read aloud in my language.
  const speech = useStore((s) => s.speech);
  const talkAvailable = ttsSupported() && speech !== 'disabled' && recordingSupported();
  const talkKey = (id: string) => `tc.talk.${id}`;
  const [talk, setTalk] = useState(false);
  const spoken = useRef(new Set<string>());
  // Only messages newer than this are read aloud (history and older pages never are).
  const floor = useRef<string | null>(null);
  const newest = () => {
    const st = getState();
    const list = st.threads[st.activeId ?? '']?.items ?? [];
    return list.length ? list[list.length - 1]!.createdAt : null;
  };
  useEffect(() => {
    stopSpeaking();
    spoken.current = new Set();
    floor.current = null;
    let on = false;
    try {
      on = !!activeId && localStorage.getItem(talkKey(activeId)) === '1';
    } catch {
      /* ignore */
    }
    setTalk(on && talkAvailable);
  }, [activeId, talkAvailable]);
  const toggleTalk = () => {
    const next = !talk;
    if (next) {
      unlockSpeech(); // iOS: speech must start from a tap
      floor.current = newest() ?? new Date(0).toISOString();
      if (activeId) void api('POST', `/api/conversations/${activeId}/talk`, { on: true }).catch(() => {});
    } else stopSpeaking();
    setTalk(next);
    try {
      if (activeId) next ? localStorage.setItem(talkKey(activeId), '1') : localStorage.removeItem(talkKey(activeId));
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    if (!talk || !thread) return;
    // First render of this conversation with talk mode on: everything already here is history.
    if (floor.current === null) {
      floor.current = items.length ? items[items.length - 1]!.createdAt : new Date(0).toISOString();
      return;
    }
    for (const m of items) {
      if (m.createdAt <= floor.current || spoken.current.has(m.id)) continue;
      if (m.senderId === me.id || m.kind !== 'text' || m.deletedAt || !m.body) {
        spoken.current.add(m.id);
        continue;
      }
      const tr = m.translation;
      if (tr && tr.status === 'pending') continue; // wait for the translation
      spoken.current.add(m.id);
      const useTr = tr?.status === 'ready' && !!tr.body;
      void speakMessage(m.id, useTr ? tr!.body! : m.body, useTr ? tr!.targetLocale : m.sourceLocale);
    }
  }, [items, talk, thread, me.id]);
  useEffect(() => () => stopSpeaking(), []);
  // "Switch on for me too" from the talk_on note in the chat.
  useEffect(() => {
    const on = (e: Event) => {
      if ((e as CustomEvent<string>).detail === activeId && !talk && talkAvailable) toggleTalk();
    };
    window.addEventListener('tc:talk-on', on);
    return () => window.removeEventListener('tc:talk-on', on);
  });

  useEffect(() => {
    setReplyTo(null);
    setEditing(null);
    setInfo(false);
    setSearchOpen(false);
    setChatQuery('');
    stick.current = true;
  }, [activeId]);

  // Scroll to and flash a message requested by search (after it has been rendered).
  const handledHighlight = useRef(0);
  useEffect(() => {
    if (!highlight || handledHighlight.current === highlight.nonce) return;
    stick.current = false;
    requestAnimationFrame(() => {
      const el = document.getElementById(`m-${highlight.id}`);
      if (!el) return;
      handledHighlight.current = highlight.nonce;
      el.scrollIntoView({ block: 'center' });
      el.classList.remove('flash');
      void el.offsetWidth; // restart the animation
      el.classList.add('flash');
      setTimeout(() => el.classList.remove('flash'), 1600);
    });
  }, [highlight, items.length]);

  // Keep the view pinned to the bottom for new messages; preserve position when older ones load.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const first = items[0]?.id;
    if (prevFirst.current && first !== prevFirst.current && !stick.current) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if (stick.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
  }, [items, pending.length]);

  useEffect(() => {
    if (items.length) scheduleMarkRead();
  }, [items.length]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    prevHeight.current = el.scrollHeight;
    if (el.scrollTop < 60 && thread?.hasMore && !thread.loading && activeId) void loadOlder(activeId);
  };

  const jump = useCallback((id: string) => {
    const el = document.getElementById(`m-${id}`);
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('flash');
      setTimeout(() => el.classList.remove('flash'), 1400);
    }
  }, []);
  const [quote, setQuote] = useState<{ text: string; locale: string } | null>(null);
  const onReply = useCallback((m: MessageDTO, q?: { text: string; locale: string } | null) => {
    setEditing(null);
    setReplyTo(m);
    setQuote(q ?? null);
  }, []);

  // ---- quoting: select part of a message → a "Quote" button appears next to the selection.
  const [selection, setSelection] = useState<{ id: string; text: string; locale: string; x: number; y: number } | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const read = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim() ?? '';
      if (!sel || sel.rangeCount === 0 || !text) return setSelection(null);
      const range = sel.getRangeAt(0);
      const el = (n: Node | null) => (n instanceof Element ? n : n?.parentElement ?? null);
      const bodyA = el(range.startContainer)?.closest('.msg .body');
      const bodyB = el(range.endContainer)?.closest('.msg .body');
      if (!bodyA || bodyA !== bodyB) return setSelection(null);
      const article = bodyA.closest('article.msg');
      const id = article?.id.replace(/^m-/, '');
      if (!id) return setSelection(null);
      const rect = range.getBoundingClientRect();
      setSelection({
        id,
        text: [...text].slice(0, 500).join(''),
        locale: bodyA.getAttribute('lang') ?? me.readingLocale,
        x: Math.min(window.innerWidth - 110, Math.max(8, rect.left + rect.width / 2 - 50)),
        y: rect.bottom + 8 > window.innerHeight - 60 ? rect.top - 44 : rect.bottom + 8,
      });
    };
    const onChange = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(read, 150);
    };
    document.addEventListener('selectionchange', onChange);
    return () => {
      document.removeEventListener('selectionchange', onChange);
      if (timer) clearTimeout(timer);
    };
  }, [me.readingLocale]);
  const quoteSelection = () => {
    if (!selection) return;
    const m = items.find((x) => x.id === selection.id);
    if (m) onReply(m, { text: selection.text, locale: selection.locale });
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  };
  const [prefill, setPrefill] = useState<{ text: string; nonce: number } | null>(null);
  const onClarify = useCallback((m: MessageDTO, question: string) => {
    setEditing(null);
    setReplyTo(m);
    setPrefill({ text: question, nonce: Date.now() });
  }, []);
  const onEdit = useCallback((m: MessageDTO) => {
    setReplyTo(null);
    setEditing(m);
  }, []);

  if (!activeId || !conv) {
    return (
      <section className="thread empty-thread">
        <div className="empty-brand">
          <img src="/logo.png" alt="" width="96" height="96" />
          <Brand />
          <p className="muted">{t('selectConversation')}</p>
        </div>
      </section>
    );
  }

  const canModerate = conv.type === 'group' && (conv.myRole === 'owner' || conv.myRole === 'admin');
  const others = conv.members.filter((m) => m.id !== me.id);
  const presenceText = (id: string | undefined) => {
    const p = id ? presence[id] : null;
    if (p === 'online') return t('activeNow');
    if (p) return t('lastSeen', { time: formatDay(p, ui) === formatDay(new Date().toISOString(), ui) ? new Date(p).toLocaleTimeString(ui, { hour: '2-digit', minute: '2-digit' }) : formatDay(p, ui) });
    return '';
  };
  const onlineCount = others.filter((o) => presence[o.id] === 'online').length;
  const subtitle =
    conv.type === 'group'
      ? [t('members', { n: conv.members.length }), onlineCount ? t('nActive', { n: onlineCount }) : ''].filter(Boolean).join(' · ')
      : [others[0]?.organizationName, presenceText(others[0]?.id)].filter(Boolean).join(' · ');
  const typers = Object.entries(typingMap ?? {})
    .filter(([uid, until]) => until > Date.now() && uid !== me.id)
    .map(([uid]) => conv.members.find((m) => m.id === uid)?.displayName)
    .filter(Boolean) as string[];

  let lastDay = '';
  let lastSender: string | null = null;

  return (
    <section
      className={`thread ${dragging ? 'dragging' : ''}`}
      aria-label={conversationTitle(conv, me.id)}
      {...swipe}
      onDragOver={(e) => {
        if ([...e.dataTransfer.types].includes('Files')) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={(e) => {
        setDragging(false);
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('tc:attach', { detail: [...e.dataTransfer.files] }));
      }}
    >
      <header className="thread-head">
        <button className="icon-btn back" onClick={() => void openConversation(null)} aria-label={t('back')}>
          <svg width="13" height="22" viewBox="0 0 13 22" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M11 2L2 11l9 9" />
          </svg>
          {otherUnread > 0 && <span className="back-count">{otherUnread > 99 ? '99+' : otherUnread}</span>}
        </button>
        <span className={`seal-stack head-avatar n${Math.min(conversationLocales(conv, me.id).length, 3)}`} aria-hidden="true" onClick={() => setInfo((v) => !v)}>
          {conversationLocales(conv, me.id).slice(0, 3).map((l) => <Seal key={l} locale={l} small />)}
        </span>
        <button className="thread-title" onClick={() => setInfo((v) => !v)} aria-expanded={!!info}>
          <span className="title">{conversationTitle(conv, me.id)}</span>
          <span className="sub">
            {conversationLocales(conv, me.id).map((l) => <Seal key={l} locale={l} small />)}
            <span>{subtitle}</span>
          </span>
        </button>
        {talkAvailable && (
          <button className={`icon-btn talk-toggle ${talk ? 'on' : ''}`} onClick={toggleTalk} aria-pressed={talk} aria-label={t('talkMode')} title={t('talkMode')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />
            </svg>
            <span className="icon-label">{t('talkShort')}</span>
          </button>
        )}
        <button className="icon-btn summary-toggle" onClick={() => setSummaryOpen(true)} aria-label={t('summarise')} title={t('summarise')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M5 6h14M5 11h14M5 16h9" />
          </svg>
          <span className="icon-label">{t('summarise')}</span>
        </button>
        <button className={`icon-btn search-toggle ${searchOpen ? 'on' : ''}`} onClick={() => setSearchOpen((v) => !v)} aria-expanded={searchOpen} aria-label={t('searchInChat')} title={t('searchInChat')}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <circle cx="8.5" cy="8.5" r="5.5" />
            <path d="M13 13l4.5 4.5" />
          </svg>
          <span className="icon-label">{t('searchShort')}</span>
        </button>
        {/* Phones: talk mode and summary sit behind "…" so the header keeps one clear action. */}
        <div className="more-anchor" ref={moreRef}>
          <button className={`icon-btn more-toggle ${moreOpen ? 'on' : ''}`} data-popup-toggle onClick={() => setMoreOpen((v) => !v)} aria-expanded={moreOpen} aria-haspopup="menu" aria-label={t('more')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="19" cy="12" r="1.9" />
            </svg>
          </button>
          {moreOpen && (
            <>
              {/* Swallows the tap that closes the menu, so it does not also press what is underneath. */}
              <div className="more-menu" role="menu">
                {talkAvailable && (
                  <button role="menuitem" className="more-talk" onClick={() => { setMoreOpen(false); toggleTalk(); }}>
                    {talk ? t('talkModeOff') : t('talkMode')}
                  </button>
                )}
                <button role="menuitem" className="more-summary" onClick={() => { setMoreOpen(false); setSummaryOpen(true); }}>{t('summarise')}</button>
                <button role="menuitem" className="more-files" onClick={() => { setMoreOpen(false); setInfo('files'); }}>{t('chatFiles')}</button>
                <button role="menuitem" onClick={() => { setMoreOpen(false); setInfo(true); }}>{t('chatInfo')}</button>
              </div>
            </>
          )}
        </div>
      </header>
      {talk && (
        <div className="talk-banner" role="status">
          <strong>{t('talkMode')}</strong> {t('talkModeHint')}
          {!hasVoice(me.readingLocale) && <span className="warn"> {t('noVoice', { lang: languageName(ui, me.readingLocale) })}</span>}
          <button className="link-btn" onClick={toggleTalk}>{t('talkModeOff')}</button>
        </div>
      )}
      <PinnedBar conv={conv} />
      {selection && (
        <button
          type="button"
          className="btn small quote-btn"
          style={{ left: selection.x, top: selection.y }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={quoteSelection}
        >
          ❝ {t('quote')}
        </button>
      )}
      {searchOpen && (
        <div className="thread-search">
          <input
            type="search"
            autoFocus
            placeholder={t('searchInChat')}
            aria-label={t('searchInChat')}
            value={chatQuery}
            onChange={(e) => setChatQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchOpen(false);
                setChatQuery('');
              }
            }}
          />
          <SearchResults query={chatQuery} conversationId={activeId} showConversation={false} />
        </div>
      )}
      <div className="thread-body">
        <div className="messages" ref={scroller} onScroll={onScroll} aria-live="polite" aria-relevant="additions">
          {thread?.loaded && !thread.hasMore && <p className="beginning">{t('beginning')}</p>}
          {thread?.hasMore && (
            <button className="btn ghost small load-more" onClick={() => void loadOlder(activeId)} disabled={thread.loading}>
              {t('loadEarlier')}
            </button>
          )}
          {items.map((m, i) => {
            const day = m.createdAt.slice(0, 10);
            const newDay = day !== lastDay;
            lastDay = day;
            const firstInRun = newDay || lastSender !== m.senderId || m.kind === 'system';
            const showSender = conv.type === 'group' && firstInRun;
            lastSender = m.kind === 'system' ? null : m.senderId;
            // Messages in a row from the same person sit close together; the last one carries the avatar.
            const next = items[i + 1];
            const lastInRun = !next || next.kind === 'system' || next.senderId !== m.senderId || next.createdAt.slice(0, 10) !== day;
            return (
              <div key={m.id}>
                {newDay && <div className="day-sep"><span>{formatDay(m.createdAt, ui)}</span></div>}
                <Message m={m} nameColor={m.senderId ? nameColors.get(m.senderId) : undefined} showSender={showSender} firstInRun={firstInRun} lastInRun={lastInRun} inGroup={conv.type === 'group'} canModerate={canModerate} onReply={onReply} onClarify={onClarify} onEdit={onEdit} onJump={jump} />
              </div>
            );
          })}
          {pending.map((o) => (
            <article key={o.clientMessageId} className={`msg mine pending ${o.state}`}>
              <div className="bubble">
                <p className="body"><Linkified text={o.body} /></p>
                <div className="msg-meta">
                  {o.state === 'sending' ? (
                    <span>{t('sending')}</span>
                  ) : (
                    <span className="failed" role="alert">
                      {t('notSent')} ({o.error}){' '}
                      <button className="link-btn" onClick={() => retryOutbox(o.clientMessageId)}>{t('retry')}</button>{' '}
                      <button className="link-btn" onClick={() => discardOutbox(o.clientMessageId)}>{t('discard')}</button>
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
        {info && <GroupInfo conv={conv} onClose={() => setInfo(false)} onJump={jump} showFiles={info === 'files'} />}
        {summaryOpen && <SummaryDialog url={`/api/conversations/${activeId}/summary`} title={t('sumChatTitle')} onClose={() => setSummaryOpen(false)} />}
      </div>
      <div className="typing-line" aria-live="polite">{typers.length ? t(typers.length === 1 ? 'typingOne' : 'typingMany', { name: typers.join(', ') }) : ''}</div>
      <Composer
        conversationId={activeId}
        replyTo={replyTo}
        quote={quote}
        editing={editing}
        prefill={prefill}
        talkMode={talk}
        onDone={() => {
          setReplyTo(null);
          setQuote(null);
          setEditing(null);
          stick.current = true;
        }}
      />
    </section>
  );
}

/**
 * iPhone-style "swipe from the left edge to go back" to the chat list, on phones (in the app, or the
 * home-screen web app where the browser has no back gesture of its own). The chat follows the finger.
 */
function useSwipeBack(onBack: () => void) {
  const start = useRef<{ x: number; y: number; el: HTMLElement } | null>(null);
  const enabled = () =>
    window.matchMedia('(max-width: 760px)').matches &&
    (document.documentElement.classList.contains('native') || window.matchMedia('(display-mode: standalone)').matches);
  return {
    onTouchStart: (e: React.TouchEvent<HTMLElement>) => {
      const t = e.touches[0];
      start.current = t && t.clientX < 28 && enabled() ? { x: t.clientX, y: t.clientY, el: e.currentTarget } : null;
    },
    onTouchMove: (e: React.TouchEvent<HTMLElement>) => {
      const s = start.current;
      const t = e.touches[0];
      if (!s || !t) return;
      const dx = t.clientX - s.x;
      if (Math.abs(t.clientY - s.y) > 60 && dx < 40) {
        start.current = null;
        s.el.style.transform = '';
        return;
      }
      if (dx > 0) s.el.style.transform = `translateX(${dx}px)`;
    },
    onTouchEnd: (e: React.TouchEvent<HTMLElement>) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const t = e.changedTouches[0];
      const dx = t ? t.clientX - s.x : 0;
      s.el.style.transition = 'transform 0.18s ease-out';
      if (dx > Math.min(120, window.innerWidth * 0.3)) {
        s.el.style.transform = 'translateX(100%)';
        setTimeout(() => {
          s.el.style.transition = '';
          s.el.style.transform = '';
          onBack();
        }, 170);
      } else {
        s.el.style.transform = '';
        setTimeout(() => (s.el.style.transition = ''), 200);
      }
    },
  };
}
